<template>
  <div class="camp-entry" :class="{ 'has-mine': canEnterMine }">
    <!-- 研修营banner -->
    <img class="camp-banner" :src="banner" alt="研修营banner">
    <!-- 省份、城市、中心特色筛选 -->
    <div class="filter-row">
      <div
        class="filter-item"
        :class="{ selected: item.text }"
        v-for="item in filterItems"
        :key="item.type"
        @click="openFilter(item.type)"
      >
        <span>{{ item.text || item.label }}</span>
        <img class="filter-arrow" src="@/assets/images/course/icon-arrowDown.png" alt="">
      </div>
    </div>
    <!-- 研修营列表 -->
    <van-list
      v-model="loading"
      :finished="finished"
      :immediate-check="false"
      finished-text="没有更多了"
      @load="loadMoreCamps"
    >
      <div
        class="camp-card"
        v-for="item in campList"
        :key="item.id"
        @click="goDetail(item)"
      >
        <img class="camp-cover" src="@/assets/images/researchCamp/banner.png" alt="">
        <div class="camp-main">
          <div class="camp-name">{{ item.name }}</div>
          <div class="camp-addr">{{ item.province }} {{ item.city }}</div>
          <div class="camp-tags">
            <span class="camp-tag" v-for="tag in item.features" :key="tag">{{ tag }}</span>
          </div>
        </div>
      </div>
    </van-list>
    <!-- 我的研修营入口 -->
    <div class="camp-bottom" v-if="canEnterMine">
      <div class="camp-mine" @click="goMine">我的研修营</div>
    </div>
    <!-- 筛选底部弹窗 -->
    <van-popup v-model="filterVisible" position="bottom" round @closed="applyPendingFilter">
      <van-picker
        :key="filterPickerKey"
        show-toolbar
        :title="filterTitle"
        :columns="filterColumns"
        :default-index="pickerDefaultIndex"
        @confirm="onFilterConfirm"
        @cancel="filterVisible = false"
      />
    </van-popup>
  </div>
</template>

<script>
import {
  getResearchCampCityOptions,
  getResearchCampContext,
  getResearchCampFeatureOptions,
  getResearchCampList,
  getResearchCampProvinceOptions
} from '@/api/researchCamp'

export default {
  name: 'CampListEntry',
  data () {
    return {
      banner: require('@/assets/images/researchCamp/banner.png'),
      filterVisible: false,
      filterPickerKey: 0,
      pickerDefaultIndex: 0,
      pendingFilter: null,
      filterType: 'province',
      provinceOptions: [],
      cityOptions: [],
      featureOptions: [],
      campList: [],
      provinceCd: '',
      provinceName: '',
      cityCd: '',
      cityName: '',
      feature: '',
      featureName: '',
      canEnterMine: false,
      loading: false,
      finished: false,
      pageNum: 1,
      pageSize: 10,
      campFilters: {},
      requestSeq: 0,
      requestInFlightSeq: null
    }
  },
  created () {
    this.loadContext()
    this.initializeFilters()
  },
  computed: {
    // 筛选项文案。有选中值时显示名称，否则显示默认标题
    filterItems () {
      return [
        { type: 'province', label: '省份', text: this.provinceName },
        { type: 'city', label: '城市', text: this.cityName },
        { type: 'feature', label: '中心特色', text: this.featureName }
      ]
    },
    // 弹窗标题
    filterTitle () {
      if (this.filterType === 'province') return '选择省份'
      if (this.filterType === 'city') return '选择城市'
      if (this.filterType === 'feature') return '选择中心特色'
      return ''
    },
    // 当前筛选项，第一项是全部
    filterColumns () {
      const options = this.filterType === 'province'
        ? this.provinceOptions
        : this.filterType === 'city' ? this.cityOptions : this.featureOptions
      const labelKey = this.filterType === 'feature' ? 'value' : 'name'
      return ['全部'].concat(options.map(item => item[labelKey]))
    }
  },
  methods: {
    // 是否展示我的研修营。需要先选营时不展示
    async loadContext () {
      try {
        const response = await getResearchCampContext()
        const context = response && response.data ? response.data : {}
        this.canEnterMine = !context.requiresCampSelection
      } catch (error) {
        this.canEnterMine = false
      }
    },
    // 先加载筛选项，再加载研修营列表
    async initializeFilters () {
      await this.loadFilterOptions()
      await this.loadCamps()
    },
    // 加载省份和中心特色
    async loadFilterOptions () {
      try {
        const response = await getResearchCampProvinceOptions()
        this.provinceOptions = response && response.data ? response.data : []
      } catch (error) {
        this.provinceOptions = []
        this.$toast('省份列表加载失败')
      }
      try {
        const response = await getResearchCampFeatureOptions()
        this.featureOptions = response && response.data ? response.data : []
      } catch (error) {
        this.featureOptions = []
        this.$toast('中心特色加载失败')
      }
    },
    // 按省份加载城市。未选省份时清空
    async loadCityOptions (provinceCd) {
      if (!provinceCd) {
        this.cityOptions = []
        return
      }
      try {
        const response = await getResearchCampCityOptions(provinceCd)
        this.cityOptions = response && response.data ? response.data : []
      } catch (error) {
        this.cityOptions = []
        this.$toast('城市列表加载失败')
      }
    },
    // 按筛选条件加载研修营列表
    async loadCamps (loadOptions, append = false) {
      const requestSeq = append ? this.requestSeq : ++this.requestSeq
      if (append && this.requestInFlightSeq === requestSeq) return
      if (!append) {
        this.campFilters = Object.assign({}, loadOptions || {})
        this.pageNum = 1
        this.finished = false
        this.campList = []
      }
      this.requestInFlightSeq = requestSeq
      this.loading = true
      try {
        const response = await getResearchCampList(Object.assign({}, this.campFilters, {
          pageNum: this.pageNum,
          pageSize: this.pageSize
        }))
        if (requestSeq !== this.requestSeq) return
        const page = response && response.data ? response.data : {}
        const rows = page.list || []
        const camps = rows.map(item => Object.assign({}, item, {
          id: item.campConfigId,
          province: item.provinceName,
          city: item.cityName,
          features: this.featureNames(item.features)
        }))
        this.campList = append ? this.campList.concat(camps) : camps
        this.pageNum = (Number(page.pageNum) || this.pageNum) + 1
        this.finished = Number(page.pageNum) >= Number(page.pages) || camps.length < this.pageSize
      } catch (error) {
        if (requestSeq !== this.requestSeq) return
        this.$toast('研修营列表加载失败')
        this.finished = true
      } finally {
        if (this.requestInFlightSeq === requestSeq) this.requestInFlightSeq = null
        if (requestSeq === this.requestSeq) this.loading = false
      }
    },
    loadMoreCamps () {
      if (!this.finished) this.loadCamps(null, true)
    },
    // 特色编码转成数组
    parseFeatures (value) {
      if (!value) return []
      if (Array.isArray(value)) return value
      return String(value).split(',').map(item => item.trim()).filter(Boolean)
    },
    // 特色编码转成字典名称
    featureNames (value) {
      const namesByCode = this.featureOptions.reduce((result, item) => {
        result[String(item.code)] = item.value
        return result
      }, {})
      return this.parseFeatures(value).map(code => namesByCode[code] || code)
    },
    // 打开对应筛选项
    openFilter (type) {
      if (type === 'city' && !this.provinceCd) {
        this.$toast('请先选择省份')
        return
      }
      this.filterType = type
      this.pickerDefaultIndex = this.resolveFilterIndex(type)
      this.filterPickerKey += 1
      this.filterVisible = true
    },
    // 当前已选项在列表中的位置，未选中时停在全部
    resolveFilterIndex (type) {
      const currentItem = this.filterItems.find(item => item.type === type)
      const currentText = currentItem && currentItem.text
      if (!currentText) return 0
      const index = this.filterColumns.indexOf(currentText)
      return index >= 0 ? index : 0
    },
    // 确认筛选后先关弹窗，再加载城市和列表，避免弹窗停在原处
    onFilterConfirm (value, index) {
      const selectedIndex = Array.isArray(index) ? index[0] : index
      const selectedName = Array.isArray(value) ? value[0] : value
      const options = this.filterType === 'province'
        ? this.provinceOptions
        : this.filterType === 'city' ? this.cityOptions : this.featureOptions
      const labelKey = this.filterType === 'feature' ? 'value' : 'name'
      const selected = selectedIndex > 0
        ? options[selectedIndex - 1]
        : options.find(item => String(item[labelKey]) === String(selectedName))
      const filters = {
        provinceCd: this.provinceCd,
        cityCd: this.cityCd,
        feature: this.feature
      }
      const provinceChanged = this.filterType === 'province'
      if (provinceChanged) {
        this.provinceCd = selected ? String(selected.code) : ''
        this.provinceName = selected ? selected.name : ''
        this.cityCd = ''
        this.cityName = ''
        filters.provinceCd = this.provinceCd
        filters.cityCd = ''
      } else if (this.filterType === 'city') {
        this.cityCd = selected ? String(selected.code) : ''
        this.cityName = selected ? selected.name : ''
        filters.cityCd = this.cityCd
      } else {
        this.feature = selected ? String(selected.code) : ''
        this.featureName = selected ? selected.value : ''
        filters.feature = this.feature
      }
      Object.keys(filters).forEach(key => {
        if (!filters[key]) delete filters[key]
      })
      this.pendingFilter = {
        filters,
        reloadCities: provinceChanged,
        provinceCd: this.provinceCd
      }
      this.filterVisible = false
    },
    // 等弹窗收起后再刷新列表，避免关闭动画和列表重绘抢在同一帧
    applyPendingFilter () {
      const pending = this.pendingFilter
      if (!pending) return
      this.pendingFilter = null
      if (pending.reloadCities) {
        this.cityOptions = []
        this.loadCityOptions(pending.provinceCd)
      }
      this.loadCamps(pending.filters)
    },
    // 跳转研修营详情。不用 campId，避免被旧训练营的路由守卫拦截
    goDetail (item) {
      this.$router.push({
        path: '/researchCamp/detail',
        query: { researchCampId: item.campConfigId }
      })
    },
    // 跳转我的研修营
    goMine () {
      this.$router.push('/researchCamp/mine')
    }
  }
}
</script>

<style lang="less" scoped>
.camp-entry {
  &.has-mine {
    padding-bottom: 68px;
  }

  .camp-banner {
    width: 100%;
    display: block;
  }

  .filter-row {
    display: flex;
    margin: 14px 0 12px;

    .filter-item {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      height: 36px;
      min-width: 0;
      margin-right: 10px;
      padding: 0 10px;
      border-radius: 18px;
      background: #F2F3F5;
      font-size: 14px;
      color: #666;
      box-sizing: border-box;

      &:last-child {
        margin-right: 0;
      }

      &.selected {
        color: #EB1700;
      }

      span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .filter-arrow {
        width: 10px;
        height: 6px;
        margin-left: 4px;
        flex-shrink: 0;
      }
    }
  }

  .camp-card {
    display: flex;
    margin-bottom: 12px;
    padding: 16px 14px;
    border-radius: 12px;
    background: #fff;

    .camp-cover {
      width: 44px;
      height: 44px;
      border-radius: 50%;
      object-fit: cover;
      margin-right: 12px;
      flex-shrink: 0;
    }

    .camp-main {
      flex: 1;
      min-width: 0;

      .camp-name {
        font-size: 14px;
        color: #000000;
        line-height: 22px;
      }

      .camp-addr {
        margin-top: 4px;
        font-size: 12px;
        color: #81766F;
        line-height: 18px;
      }

      .camp-tags {
        display: flex;
        flex-wrap: wrap;

        .camp-tag {
          margin: 6px 6px 0 0;
          padding: 0 10px;
          height: 20px;
          line-height: 20px;
          border-radius: 10px;
          font-size: 10px;
          color: #FF5B0C;
          background: #ff5b0c1a;
          box-sizing: border-box;
        }
      }
    }
  }

  .camp-bottom {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 16px;
    background: #FFFFFF;

    .camp-mine {
      height: 40px;
      line-height: 40px;
      text-align: center;
      border-radius: 20px;
      background: #EB1700;
      color: #fff;
      font-size: 14px;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/views/researchCamp/components/CampListEntry.vue